import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * What the glyph says about the state. `neutral` is calm and grey: nothing here
 * yet. `warning` is the classic amber for "something is off but not broken",
 * so a warning triangle never reads as a decoration. `danger` is for a state
 * that stops the reader.
 */
export type EmptyStateTone = "neutral" | "warning" | "danger";

const RING: Record<EmptyStateTone, string> = {
  neutral: "border-border bg-muted/40",
  warning: "border-warning/40 bg-warning/10",
  danger: "border-destructive/40 bg-destructive/10",
};

const GLYPH: Record<EmptyStateTone, string> = {
  neutral: "text-muted-foreground",
  warning: "text-warning",
  danger: "text-destructive",
};

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  tone?: EmptyStateTone;
  /**
   * `page` fills a page or a pane. `compact` is the same state inside a card
   * or a section: the same well, heading and copy, at the size of the thing
   * it sits in, so an empty card does not shout louder than a full one.
   */
  size?: "page" | "compact";
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
    /**
     * Why the action cannot be taken here. The button stays in view, disabled,
     * with the reason written under it rather than hidden in a hover.
     */
    disabledReason?: string;
  };
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  tone = "neutral",
  size = "page",
}: EmptyStateProps) {
  const compact = size === "compact";
  return (
    // Calm and deliberate, not broken: a hairline-bordered well rather than a
    // flat grey disc, a real heading in the display face, and body copy at the
    // same size as everywhere else so it does not read as an error notice.
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-2.5 py-6" : "gap-4 py-20",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-full border",
          compact ? "h-9 w-9" : "h-14 w-14",
          RING[tone],
        )}
      >
        <Icon
          className={cn(compact ? "h-4 w-4" : "h-6 w-6", GLYPH[tone])}
          strokeWidth={1.5}
        />
      </div>
      <div className={compact ? "space-y-1" : "space-y-2"}>
        <h3
          className={cn(
            "text-foreground",
            compact ? "text-sm font-medium" : "text-base",
          )}
        >
          {title}
        </h3>
        {description && (
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      {action && <EmptyStateAction action={action} compact={compact} />}
    </div>
  );
}

function EmptyStateAction({
  action,
  compact,
}: {
  action: NonNullable<EmptyStateProps["action"]>;
  compact: boolean;
}) {
  const spacing = compact ? undefined : "mt-1";
  if (action.disabledReason) {
    return (
      <div className={cn("flex flex-col items-center gap-1.5", spacing)}>
        <Button size="sm" disabled>
          {action.label}
        </Button>
        <p className="max-w-sm text-xs text-muted-foreground">
          {action.disabledReason}
        </p>
      </div>
    );
  }
  if (action.href) {
    return (
      <Button asChild size="sm" className={spacing}>
        <Link to={action.href}>{action.label}</Link>
      </Button>
    );
  }
  return (
    <Button size="sm" className={spacing} onClick={action.onClick}>
      {action.label}
    </Button>
  );
}
